// =====================================================================================================================
// ========== COMPANIES FORM
//
// Data flow rationale — mirrors the pattern established in LeasesForm:
//
//  1. STATIC OPTIONS (companies list + persons list) are fetched once on mount via Promise.all.
//     These are used to populate the owner Autocomplete dropdowns.
//
//  2. COMPANY-SPECIFIC DATA (the company row + its owners) is fetched only when `selectedCompanyId` changes
//     AND after static options have loaded (`isLoadingOptions === false` guard).  This prevents a race where
//     owner option objects don't yet exist when we try to hydrate the form.
//
//  3. OWNERS UX — redesigned to match the "Locataires" pattern in LeasesForm:
//     • MUI Autocomplete with grouped options (Entreprises / Personnes) replaces the flat Select.
//     • A numeric "Parts" field sits beside each Autocomplete row.
//     • The awkward `"companyId/personId"` string encoding is now purely an internal value key on the
//       option object, not visible to the user.
//
//  4. MUTATIONS call Supabase directly.  Errors are caught, parsed into French prose, and shown in an
//     Alert inside the card so the user never needs dev-tools.
//
//  5. CANCEL FLAGS (`cancelled = true` in cleanup) prevent stale-state updates when `selectedCompanyId`
//     changes rapidly or the component unmounts mid-fetch.
// =====================================================================================================================

import React, { useState, useEffect, useMemo, useCallback } from "react"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CircularProgress from "@mui/material/CircularProgress"
import DeleteIcon from "@mui/icons-material/Delete"
import FormLabel from "@mui/material/FormLabel"
import Grid from "@mui/material/Grid2"
import IconButton from "@mui/material/IconButton"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Select from "@mui/material/Select"
import Skeleton from "@mui/material/Skeleton"
import TextField from "@mui/material/TextField"
import { darken, lighten, styled } from "@mui/system"

// =====================================================================================================================
// ========== CONSTANTS
// =====================================================================================================================

const LEGAL_STATUSES = [
    "Association Loi 1901",
    "Auto-Entrepreneur",
    "EURL",
    "GIE",
    "SA",
    "SARL",
    "SAS",
    "SCI",
    "SCPI",
    "SNC",
]

// A blank owner row — `value` mirrors the internal encoding used in LeasesForm tenant options.
const DEFAULT_OWNER = { id: null, kind: "person", label: "", value: "undefined/undefined", shares: "" }

const DEFAULT_COMPANY = {
    name:      "",
    status:    "",
    address_1: "",
    address_2: "",
    city:      "",
    zip:       "",
    country:   "",
}

// =====================================================================================================================
// ========== STYLED COMPONENTS
// =====================================================================================================================

const FormGrid = styled(Grid)(() => ({
    display:       "flex",
    flexDirection: "column",
}))

// Sticky group headers inside the Autocomplete dropdown (same as LeasesForm)
const GroupHeader = styled("div")(({ theme }) => ({
    position:        "sticky",
    top:             "-8px",
    padding:         "4px 10px",
    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
    ...theme.applyStyles("dark", {
        backgroundColor: darken(theme.palette.secondary.main, 0.8),
    }),
}))

const GroupItems = styled("ul")({ padding: 0 })

// =====================================================================================================================
// ========== SKELETON
//
// Rationale: field-by-field skeleton mirrors the real form layout so there is zero layout shift
// when real content arrives, giving the user a clear sense of what is loading.
// =====================================================================================================================

function FormSkeleton() {
    const field = (xs) => (
        <FormGrid size={{ xs }}>
            <Skeleton width="45%" height={16} sx={{ mb: 0.5 }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        </FormGrid>
    )
    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>
                    {field(5)}
                    {field(7)}
                    {field(12)}
                    {field(12)}
                    {field(5)}
                    {field(7)}
                    {field(12)}
                    {/* owners section */}
                    <FormGrid size={{ xs: 12 }}>
                        <Skeleton width="35%" height={16} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    <FormGrid size={{ xs: 12 }} sx={{ mt: 1 }}>
                        <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                </Grid>
            </CardContent>
        </Card>
    )
}

// =====================================================================================================================
// ========== HELPERS
// =====================================================================================================================

/** Parse a Supabase / PostgreSQL error into a human-readable French message. */
function parseError(err) {
    if (!err) return null
    // Foreign-key violation — e.g. trying to delete a company that is still an owner elsewhere
    if (err.code === "23503") {
        return "Impossible de supprimer cette entreprise : elle est encore référencée dans d'autres données (propriétaires, baux, etc.)."
    }
    // Unique-constraint violation
    if (err.code === "23505") {
        return "Une entreprise avec ce nom existe déjà."
    }
    return err.message ?? "Une erreur inattendue s'est produite."
}

/**
 * Build the flat option list for the owner Autocomplete.
 * Each option carries a `kind` field used for grouping and a `value` key for equality checks.
 */
function buildOwnerOptions(companies, persons) {
    const companyOpts = (companies ?? [])
        .map((c) => ({ id: c.id, kind: "company", label: c.name, value: `${c.id}/undefined` }))
        .sort((a, b) => a.label.localeCompare(b.label))

    const personOpts = (persons ?? [])
        .map((p) => ({
            id:    p.id,
            kind:  "person",
            // Match the original display format: "M. Dupont (Jean)"
            label: [p.title, p.last_name].filter(Boolean).join(" ") + ` (${p.first_name ?? ""})`,
            value: `undefined/${p.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return [...companyOpts, ...personOpts]
}

// =====================================================================================================================
// ========== COMPANIES FORM COMPONENT
// =====================================================================================================================

export default function CompaniesForm({ supabase, selectedCompanyId, onSuccess }) {

    // ── STATIC OPTIONS ───────────────────────────────────────────────────────────────────────────────────
    // Fetched once on mount; all companies and persons are needed to populate the owner Autocomplete.

    const [ownerOptions,     setOwnerOptions]     = useState([])
    const [isLoadingOptions, setIsLoadingOptions] = useState(true)
    const [optionsError,     setOptionsError]     = useState(null)

    useEffect(() => {
        let cancelled = false
        const fetchOptions = async () => {
            setIsLoadingOptions(true)
            setOptionsError(null)
            try {
                const [companiesRes, personsRes] = await Promise.all([
                    supabase.from("company").select("id, name").order("name"),
                    supabase.from("person").select("id, first_name, last_name, title").order("last_name").order("first_name"),
                ])
                if (companiesRes.error) throw companiesRes.error
                if (personsRes.error)   throw personsRes.error
                if (!cancelled) {
                    setOwnerOptions(buildOwnerOptions(companiesRes.data, personsRes.data))
                }
            } catch (err) {
                if (!cancelled) setOptionsError(parseError(err))
            } finally {
                if (!cancelled) setIsLoadingOptions(false)
            }
        }
        fetchOptions()
        return () => { cancelled = true }
    }, [supabase])

    // ── FORM STATE ───────────────────────────────────────────────────────────────────────────────────────

    const [company,      setCompany]      = useState(DEFAULT_COMPANY)
    const [owners,       setOwners]       = useState([DEFAULT_OWNER])
    const [isLoadingForm, setIsLoadingForm] = useState(false)
    const [isSubmitting,  setIsSubmitting]  = useState(false)
    const [mutationError, setMutationError] = useState(null)

    // ── COMPANY-SPECIFIC FETCH ───────────────────────────────────────────────────────────────────────────
    // Runs when selectedCompanyId changes AND after options are ready (so ownerOptions is populated,
    // allowing us to find the matching option object for each saved owner).

    useEffect(() => {
        if (isLoadingOptions) return  // Wait for ownerOptions to be populated first

        if (!selectedCompanyId) {
            // No selection → reset to blank "add" form
            setCompany(DEFAULT_COMPANY)
            setOwners([DEFAULT_OWNER])
            setMutationError(null)
            return
        }

        let cancelled = false
        const fetchCompanyData = async () => {
            setIsLoadingForm(true)
            setMutationError(null)
            try {
                const [companyRes, ownersRes] = await Promise.all([
                    supabase.from("company").select("*").eq("id", selectedCompanyId).single(),
                    supabase
                        .from("company_owner")
                        .select(`
                            id,
                            shares,
                            owner_company_id,
                            owner_person_id,
                            owner_company:company!owner_company_id ( id, name ),
                            owner_person:person!owner_person_id ( id, first_name, last_name, title )
                        `)
                        .eq("company_id", selectedCompanyId)
                        .order("id"),
                ])

                if (companyRes.error) throw companyRes.error
                if (ownersRes.error)  throw ownersRes.error

                if (!cancelled) {
                    // Strip audit columns that should not appear in the form state
                    const { id: _id, created_at: _c, updated_at: _u, ...companyFields } = companyRes.data
                    setCompany(companyFields)

                    // Hydrate each saved owner into a form owner object, resolving the option label.
                    const formattedOwners = (ownersRes.data ?? []).map((o) => {
                        const isCompany = !!o.owner_company_id
                        const valueKey  = isCompany
                            ? `${o.owner_company_id}/undefined`
                            : `undefined/${o.owner_person_id}`

                        // Prefer the label from ownerOptions so it is consistent with the dropdown display
                        const matchedOption = ownerOptions.find((opt) => opt.value === valueKey)
                        const label = matchedOption?.label
                            ?? (isCompany
                                ? o.owner_company?.name ?? ""
                                : [o.owner_person?.title, o.owner_person?.last_name].filter(Boolean).join(" ")
                                + ` (${o.owner_person?.first_name ?? ""})`)

                        return {
                            id:    o.id,
                            kind:  isCompany ? "company" : "person",
                            label,
                            value: valueKey,
                            shares: o.shares ?? "",
                        }
                    })

                    setOwners(formattedOwners.length > 0 ? formattedOwners : [DEFAULT_OWNER])
                }
            } catch (err) {
                if (!cancelled) setMutationError(parseError(err))
            } finally {
                if (!cancelled) setIsLoadingForm(false)
            }
        }

        fetchCompanyData()
        return () => { cancelled = true }
        // ownerOptions is intentionally excluded from deps: it is stable after options load,
        // and including it would cause a double-fetch on initial mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyId, isLoadingOptions, supabase])

    // ── FORM CHANGE HANDLERS ─────────────────────────────────────────────────────────────────────────────

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target
        setCompany((prev) => ({ ...prev, [name]: value }))
    }, [])

    const addOwner = useCallback(() => {
        setOwners((prev) => [...prev, DEFAULT_OWNER])
    }, [])

    const removeOwner = useCallback((index) => {
        setOwners((prev) => prev.filter((_, i) => i !== index))
    }, [])

    /** Update the Autocomplete selection for an owner row, preserving its shares value. */
    const handleOwnerEntityChange = useCallback((index, option) => {
        setOwners((prev) =>
            prev.map((o, i) =>
                i === index ? { ...option, shares: o.shares } : o
            )
        )
    }, [])

    /** Update only the shares field for an owner row. */
    const handleOwnerSharesChange = useCallback((index, value) => {
        setOwners((prev) =>
            prev.map((o, i) => (i === index ? { ...o, shares: value } : o))
        )
    }, [])

    // ── RESET ────────────────────────────────────────────────────────────────────────────────────────────

    const resetForm = useCallback(() => {
        setCompany(DEFAULT_COMPANY)
        setOwners([DEFAULT_OWNER])
        setMutationError(null)
    }, [])

    // ── OWNER INSERT HELPER ──────────────────────────────────────────────────────────────────────────────
    // Shared by insert and update paths.

    const insertOwners = useCallback(async (companyId, ownerRows) => {
        // Build clean DB rows, skipping blank/unselected entries
        const dbRows = ownerRows
            .map((o) => {
                const [rawCompany, rawPerson] = o.value.split("/")
                const ownerCompanyId = rawCompany === "undefined" ? undefined : rawCompany
                const ownerPersonId  = rawPerson  === "undefined" ? undefined : rawPerson
                if (!ownerCompanyId && !ownerPersonId) return null  // blank row
                return Object.fromEntries(
                    Object.entries({
                        company_id:       companyId,
                        owner_company_id: ownerCompanyId,
                        owner_person_id:  ownerPersonId,
                        shares:           o.shares !== "" ? parseInt(o.shares, 10) : undefined,
                    }).filter(([, v]) => v !== undefined && v !== null)
                )
            })
            .filter(Boolean)

        if (dbRows.length > 0) {
            const { error } = await supabase.from("company_owner").insert(dbRows)
            if (error) throw error
        }
    }, [supabase])

    // ── SUBMIT ───────────────────────────────────────────────────────────────────────────────────────────

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true)
        setMutationError(null)
        try {
            if (selectedCompanyId) {
                // ── UPDATE ──
                // Strip read-only / audit fields before sending to Supabase
                const { id: _id, created_at: _c, updated_at: _u, ...updatePayload } = company
                const { error: updateError } = await supabase
                    .from("company")
                    .update(updatePayload)
                    .eq("id", selectedCompanyId)
                if (updateError) throw updateError

                // Replace all owners: delete existing then re-insert.
                // This is the simplest correct strategy when partial updates could leave orphan rows.
                const { error: delError } = await supabase
                    .from("company_owner")
                    .delete()
                    .eq("company_id", selectedCompanyId)
                if (delError) throw delError

                await insertOwners(selectedCompanyId, owners)
            } else {
                // ── INSERT ──
                const { data: inserted, error: insertError } = await supabase
                    .from("company")
                    .insert(company)
                    .select("id")
                    .single()
                if (insertError) throw insertError

                await insertOwners(inserted.id, owners)
            }

            resetForm()
            onSuccess?.()
        } catch (err) {
            setMutationError(parseError(err))
        } finally {
            setIsSubmitting(false)
        }
    }, [company, owners, selectedCompanyId, supabase, insertOwners, resetForm, onSuccess])

    // ── DELETE ───────────────────────────────────────────────────────────────────────────────────────────

    const handleDelete = useCallback(async () => {
        if (!selectedCompanyId) return
        setIsSubmitting(true)
        setMutationError(null)
        try {
            // Delete child rows first to avoid FK constraint errors on the company row.
            const { error: delOwnersError } = await supabase
                .from("company_owner")
                .delete()
                .eq("company_id", selectedCompanyId)
            if (delOwnersError) throw delOwnersError

            const { error: delCompanyError } = await supabase
                .from("company")
                .delete()
                .eq("id", selectedCompanyId)
            if (delCompanyError) throw delCompanyError

            resetForm()
            onSuccess?.()
        } catch (err) {
            // This surfaces FK violations clearly — e.g. if the company is still referenced in a lease.
            setMutationError(parseError(err))
        } finally {
            setIsSubmitting(false)
        }
    }, [selectedCompanyId, supabase, resetForm, onSuccess])

    // ── RENDER: loading states ────────────────────────────────────────────────────────────────────────────

    if (isLoadingOptions) return <FormSkeleton />
    if (optionsError)     return <Alert severity="error">{optionsError}</Alert>
    if (isLoadingForm)    return <FormSkeleton />

    // ── RENDER: form ─────────────────────────────────────────────────────────────────────────────────────

    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>

                    {/* ── MUTATION ERROR BANNER ── */}
                    {mutationError && (
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="error" onClose={() => setMutationError(null)}>
                                {mutationError}
                            </Alert>
                        </Grid>
                    )}

                    {/* ── STATUT ── */}
                    <FormGrid size={{ xs: 5 }}>
                        <FormLabel htmlFor="company-status" required>
                            Statut
                        </FormLabel>
                        <Select
                            id="company-status"
                            name="status"
                            value={company.status ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        >
                            {LEGAL_STATUSES.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormGrid>

                    {/* ── NOM ── */}
                    <FormGrid size={{ xs: 7 }}>
                        <FormLabel htmlFor="company-name" required>
                            Nom
                        </FormLabel>
                        <OutlinedInput
                            id="company-name"
                            name="name"
                            placeholder="Entreprise XYZ"
                            value={company.name ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* ── ADRESSE ── */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="address1" required>
                            Adresse
                        </FormLabel>
                        <OutlinedInput
                            id="address1"
                            name="address_1"
                            placeholder="55 Rue du Faubourg Saint-Honoré"
                            value={company.address_1 ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* ── COMPLÉMENT D'ADRESSE ── */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="address2">
                            Complément d'adresse
                        </FormLabel>
                        <OutlinedInput
                            id="address2"
                            name="address_2"
                            placeholder="Bureau #1"
                            value={company.address_2 ?? ""}
                            onChange={handleInputChange}
                            size="small"
                        />
                    </FormGrid>

                    {/* ── CODE POSTAL ── */}
                    <FormGrid size={{ xs: 5 }}>
                        <FormLabel htmlFor="zip" required>
                            Code postal
                        </FormLabel>
                        <OutlinedInput
                            id="zip"
                            name="zip"
                            placeholder="75008"
                            value={company.zip ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* ── VILLE ── */}
                    <FormGrid size={{ xs: 7 }}>
                        <FormLabel htmlFor="city" required>
                            Ville
                        </FormLabel>
                        <OutlinedInput
                            id="city"
                            name="city"
                            placeholder="Paris"
                            value={company.city ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* ── PAYS ── */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="country" required>
                            Pays
                        </FormLabel>
                        <OutlinedInput
                            id="country"
                            name="country"
                            placeholder="France"
                            value={company.country ?? ""}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* ── PROPRIÉTAIRES ──
                        Rationale: Autocomplete (grouped by kind) replaces the flat Select used
                        previously.  Grouping makes it easy to distinguish companies from persons
                        in a long list.  The `value` / `isOptionEqualToValue` pair guarantees
                        controlled behaviour even when the option list re-renders.  A separate
                        numeric input for "Parts" sits to the right, matching the original schema. */}
                    <FormGrid container spacing={1} size={{ xs: 12 }}>
                        <FormLabel required>Propriétaires</FormLabel>

                        {owners.map((owner, index) => (
                            <Grid container key={index} spacing={1} alignItems="center">

                                {/* Entity picker */}
                                <FormGrid size={{ xs: 7 }}>
                                    <Autocomplete
                                        disablePortal
                                        disableClearable
                                        options={ownerOptions}
                                        groupBy={(opt) => opt?.kind}
                                        getOptionLabel={(opt) => opt?.label ?? ""}
                                        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                                        value={
                                            ownerOptions.find((o) => o.value === owner.value)
                                            ?? { ...DEFAULT_OWNER, label: owner.label }
                                        }
                                        onChange={(_e, newVal) => handleOwnerEntityChange(index, newVal)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Sélectionnez un propriétaire"
                                                size="small"
                                            />
                                        )}
                                        renderGroup={(params) => (
                                            <li key={params.key}>
                                                <GroupHeader>
                                                    {params.group === "company" ? "Entreprises" : "Personnes"}
                                                </GroupHeader>
                                                <GroupItems>{params.children}</GroupItems>
                                            </li>
                                        )}
                                    />
                                </FormGrid>

                                {/* Shares number input */}
                                <FormGrid size={{ xs: 3 }}>
                                    <OutlinedInput
                                        placeholder="Parts"
                                        value={owner.shares}
                                        onChange={(e) => handleOwnerSharesChange(index, e.target.value)}
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                    />
                                </FormGrid>

                                {/* Remove button */}
                                <FormGrid size={{ xs: 2 }}>
                                    <IconButton
                                        color="error"
                                        onClick={() => removeOwner(index)}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </FormGrid>

                            </Grid>
                        ))}

                        <Button variant="text" onClick={addOwner} size="small">
                            Ajouter un propriétaire
                        </Button>
                    </FormGrid>

                    {/* ── ACTIONS ── */}
                    <FormGrid size={{ xs: selectedCompanyId ? 6 : 12 }} sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Valider
                        </Button>
                    </FormGrid>

                    {selectedCompanyId && (
                        <FormGrid size={{ xs: 6 }} sx={{ mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={isSubmitting}
                            >
                                Supprimer
                            </Button>
                        </FormGrid>
                    )}

                </Grid>
            </CardContent>
        </Card>
    )
}